import { useTranslate, HttpError } from '@refinedev/core';
import { Edit, useForm } from '@refinedev/antd';
import { Form, Input, Radio, InputNumber, Switch, Tabs, Divider } from 'antd';
import type {
  CategoryDetailOut,
  CategoryUpdateIn,
} from '@/api/generated/schemas';
import { ModStatus, ImageKind } from '@/api/generated/schemas';
import { CollectionImageFields } from '@/components/common/CollectionImageFields';
import { ImageUploadField } from '@/components/common/ImageUploadField';
import {
  CollectionDataFields,
  normalizeCollectionData,
} from '@/components/query-builder';
import { LabelsField } from '@/components/common/LabelsField';
import { SearchPopularityField } from '@/components/common/SearchPopularityField';
import { OrderedSectionCardsField } from '@/components/user_catalog/OrderedSectionCardsField';
import {
  normalizeSectionRows,
  sectionRowsFromRecord,
  type SectionRow,
} from '@/components/user_catalog/sections/sectionRows';

export const CategoryEdit = () => {
  const t = useTranslate();
  const { formProps, saveButtonProps, form, query } = useForm<
    CategoryDetailOut,
    HttpError,
    CategoryUpdateIn
  >({
    resource: 'user_catalog.categories',
    mutationMeta: { method: 'patch' },
  });
  const record = query?.data?.data;

  // Form state holds `SectionRow`s, seeded by `sectionRowsFromRecord` — the
  // payload type only names the shape they normalise back into.
  const handleFinish = (values: CategoryUpdateIn) => {
    return formProps.onFinish?.(
      normalizeCollectionData({
        ...values,
        sections: normalizeSectionRows(values.sections as SectionRow[]),
      })
    );
  };

  // Rows edit in section order regardless of server row order.
  const initialValues = {
    ...formProps.initialValues,
    sections: sectionRowsFromRecord(record?.sections),
  };

  return (
    <Edit saveButtonProps={saveButtonProps} canDelete={!record?.is_system}>
      <Form
        {...formProps}
        initialValues={initialValues}
        onFinish={handleFinish}
        layout="vertical"
      >
        <Tabs
          items={[
            {
              key: 'basic',
              label: t('base.sections.basic_info'),
              forceRender: true,
              children: (
                <>
                  <Form.Item
                    name="name"
                    label={t('user_catalog.categories.fields.name')}
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    name="position"
                    label={t('user_catalog.categories.fields.position')}
                    rules={[{ required: true }]}
                  >
                    <InputNumber style={{ width: '100%' }} min={0} />
                  </Form.Item>

                  <Form.Item
                    name="description"
                    label={t('user_catalog.categories.fields.description')}
                  >
                    <Input.TextArea rows={4} />
                  </Form.Item>

                  <LabelsField />

                  <Form.Item
                    name="is_featured"
                    label={t('user_catalog.categories.fields.is_featured')}
                    valuePropName="checked"
                    rules={[{ required: true }]}
                  >
                    <Switch />
                  </Form.Item>

                  <Form.Item name="image_storage_key" hidden>
                    <Input />
                  </Form.Item>
                  <ImageUploadField
                    imageKind={ImageKind.categories}
                    currentUrl={record?.image_url}
                    onUpload={(key) =>
                      form?.setFieldsValue({ image_storage_key: key })
                    }
                    label={t('user_catalog.categories.fields.image_url')}
                  />

                  <Divider>
                    {t('user_catalog.categories.sections.title')}
                  </Divider>

                  <OrderedSectionCardsField
                    categoryId={record?.id}
                    existingCollectionIds={record?.sections?.map(
                      (section) => section.collection_id
                    )}
                  />

                  <Divider>{t('base.sections.system_info')}</Divider>

                  <Form.Item
                    name="mod_status"
                    label={t('base.fields.mod_status')}
                    rules={[{ required: true }]}
                  >
                    <Radio.Group
                      optionType="button"
                      buttonStyle="solid"
                      options={Object.values(ModStatus).map((value) => ({
                        label: t(`base.mod_status.${value}`),
                        value,
                      }))}
                    />
                  </Form.Item>
                </>
              ),
            },
            {
              key: 'collection',
              label: t('base.sections.collection_info'),
              forceRender: true,
              children: (
                <>
                  <Form.Item
                    name="header"
                    label={t('user_catalog.categories.fields.header')}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    name="content"
                    label={t('user_catalog.categories.fields.content')}
                  >
                    <Input.TextArea rows={4} />
                  </Form.Item>

                  <CollectionImageFields
                    coverUrl={record?.cover_url}
                    bannerUrl={record?.banner_url}
                  />

                  <CollectionDataFields mode="edit" />
                </>
              ),
            },
            {
              key: 'search',
              label: t('user_catalog.common.tabs.search'),
              forceRender: true,
              children: <SearchPopularityField />,
            },
          ]}
        />
      </Form>
    </Edit>
  );
};
